import React, { useEffect, useState, useRef } from "react";
import { Source, Layer } from "react-map-gl/mapbox";
import {
  fetchCachedRoute,
  fetchCachedRouteWithWaypoints,
} from "../services/firebase";

// Haversine distance (km)
const haversineKm = (a, b) => {
  const R = 6371;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLon = ((b[0] - a[0]) * Math.PI) / 180;
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const c =
    2 *
    Math.atan2(
      Math.sqrt(
        sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon
      ),
      Math.sqrt(
        1 -
          (sinDLat * sinDLat +
            Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon)
      )
    );
  return R * c;
};

const RouteSource = ({
  start,
  end,
  waypoints,
  disableApi = false,
  onRouteFound,
  sourceId = "route",
}) => {
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const lastWaypointsKey = useRef(null);

  // You'll need to get a free Mapbox access token from https://account.mapbox.com/
  const MAPBOX_ACCESS_TOKEN =
    "pk.eyJ1Ijoibnh0aW50ZXJhY3RpdmUiLCJhIjoiY21laXU1cHIxMDRzdDJrczY5cWhjOGhwMCJ9.RUKpP8_lBssBuBo8DhCmfA";

  useEffect(() => {
    if (!start || !end) return;

    // Prevent duplicate API calls if waypoints haven't changed
    // Include start/end coordinates in the key to ensure route refreshes when needed
    const waypointsKey = waypoints 
      ? JSON.stringify({ waypoints, start, end }) 
      : JSON.stringify({ start, end });
    
    if (lastWaypointsKey.current === waypointsKey) {
      console.log(
        "🛣️ [ROUTE SOURCE] Route unchanged, skipping duplicate API call"
      );
      return;
    }
    lastWaypointsKey.current = waypointsKey;

    const buildManualRoute = () => {
      const coords =
        Array.isArray(waypoints) && waypoints.length >= 2
          ? waypoints
          : [start, end];

      // Calculate total distance by summing segment distances
      let totalKm = 0;
      for (let i = 1; i < coords.length; i++) {
        const segmentDistance = haversineKm(coords[i - 1], coords[i]);
        totalKm += segmentDistance;
      }

      const manual = {
        geometry: {
          type: "LineString",
          coordinates: coords,
        },
        distance: totalKm, // km
        duration: null,
      };

      setRouteData(manual);
      if (onRouteFound) {
        onRouteFound({ distance: manual.distance, duration: manual.duration });
      }
    };

    const fetchRoute = async () => {
      setLoading(true);
      try {
        if (Array.isArray(waypoints) && waypoints.length >= 2) {
          // Prefer API to snap detour waypoints to real roads
          try {
            console.log(
              "🛣️ [ROUTE SOURCE] Calling Mapbox API with waypoints:",
              waypoints
            );
            const route = await fetchCachedRouteWithWaypoints(
              waypoints,
              MAPBOX_ACCESS_TOKEN
            );
            console.log("✅ [ROUTE SOURCE] Mapbox API returned:", route);
            setRouteData(route);
            if (onRouteFound) {
              onRouteFound({
                distance: route.distance,
                duration: route.duration,
              });
            }
          } catch (e) {
            console.error(
              "❌ [ROUTE SOURCE] Multi-waypoint API failed:",
              e.message
            );
            console.warn(
              "⚠️ [ROUTE SOURCE] Falling back to manual polyline (no road-following)"
            );

            buildManualRoute();
          }
        } else {
          // Use cached route fetching via Mapbox Directions
          try {
            console.log(
              "🛣️ [ROUTE SOURCE] Calling Mapbox API with start/end:",
              { start, end }
            );
            const route = await fetchCachedRoute(
              start,
              end,
              MAPBOX_ACCESS_TOKEN
            );
            console.log("✅ [ROUTE SOURCE] Mapbox API returned:", route);
            setRouteData(route);
            if (onRouteFound) {
              onRouteFound({
                distance: route.distance,
                duration: route.duration,
              });
            }
          } catch (e) {
            console.error(
              "❌ [ROUTE SOURCE] Single route API failed:",
              e.message
            );
            console.warn(
              "⚠️ [ROUTE SOURCE] Falling back to straight line (no road-following)"
            );
            // Fallback to straight line if API fails
            setRouteData({
              geometry: {
                type: "LineString",
                coordinates: [start, end],
              },
              distance: null,
              duration: null,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching route:", error);
        // Fallback to straight line if API fails
        setRouteData({
          geometry: {
            type: "LineString",
            coordinates: [start, end],
          },
          distance: null,
          duration: null,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRoute();
  }, [start, end, waypoints, disableApi, onRouteFound]);

  if (!routeData) {
    return null;
  }

  return (
    <Source
      id={sourceId}
      type="geojson"
      data={{
        type: "Feature",
        properties: {},
        geometry: routeData.geometry,
      }}
    >
      {/* Main route line */}
      <Layer
        id={`${sourceId}-layer`}
        type="line"
        paint={{
          "line-color": "#007cbf", // Blue for main route
          "line-width": 3, // Normal thickness
          "line-opacity": 0.8, // Normal opacity
          "line-dasharray": [2, 2], // Dashed pattern
        }}
        layout={{
          "line-join": "round",
          "line-cap": "round",
        }}
      />
    </Source>
  );
};

export default RouteSource;
